// The WARDROOM section of the hub (R229).
//
// A captain is the only thing an account carries from one season into the
// next, so this section is the account's durable roster and nothing else.
//
// **The section shell** (`../sectionshell.ts`, ui-map-interface.md §16.9
// and §16.16, card 05 of mockup round 092, R1904). Five sections: ROSTERS
// holds the three rosters behind one toggle row CORE · EVENT · CLAN WAR,
// as the HONOUR toggle of RANKS does; CRYO FREEZE, CAPTAIN DESIGNER, FLEET
// NAMES and FLEET LIVERY are one page each (R2186). Each page has one section, so its
// body draws no box and no second title, and a tier tag stands in the page
// title (R1945). Each rail row and tile states a live summary from the
// reads below, and none where the read has not answered.
//
// **The DOSSIER** is the companion column of ROSTERS and CRYO FREEZE: the
// picked captain at a large portrait, its phase, its age, its seasons, its
// talents and its six abilities against the training cap (R1118). A press
// on a roster tile picks the captain, for every account; with no pick the
// dossier shows the first captain of the roster the toggle shows. Under
// the room for the column the dossier of ROSTERS folds after the page, and
// on a telephone a press on a tile opens it as a sub-page of ROSTERS under
// ‹ ROSTERS, with one scroll, and ‹ ROSTERS gives the roster back at the
// place the reader left (R1919).
// The column of CAPTAIN DESIGNER is the live preview of the form. The
// column of FLEET NAMES is the naming diagram (`fleetdiagram.ts`): one
// example name in its four parts, built from the reader's faction letter
// and the first name of the draft pool. The column of FLEET LIVERY is the
// livery on one hull and the first name, as the form drafts them, before a
// SAVE (R1912, R2186). All three fold over the page, a short form of the
// diagram on a telephone. A telephone root carries a strip of portraits over
// the tiles.
//
// What it reads live:
//   - `GET /api/wardroom?mode=core` — the roster every account holds.
//   - `GET /api/wardroom?mode=event` — the roster the event worlds play
//     with. It is read whether the account holds the premium key or not,
//     because the route answers this account's own event roster and that
//     roster is empty without one. The panel fades the block and never the
//     reason (`hub.css`, R229).
//   - `GET /api/wardroom?mode=clanwar` — the roster the Clan War worlds
//     play with (R745).
//
// The figures are the server's. A talent travels as an attribute index
// and an attribute as its points, so the dossier prints the short names
// of the in-game captain card and the points against `ATTR_TRAIN_CAP`,
// and computes no bonus: the rule tables reach a world client alone. The
// portrait (R1180) is the face the Captain Designer froze (R1240), else
// the face of the name and the two talents, and it wears the account's
// banner on the faction dot where one is known.
//
// The Captain Designer (monetization.md §3.1, R1240, R1241) edits the
// picked captain. With the `captain_designer` key the page holds a strip
// of every captain over the form, and the form edits one draft of its
// looks (`designer.ts`) and sends it whole with one press of SAVE. RESET
// TO DEFAULT takes two presses (R837). A refusal stands beside the field
// it names. A captain that a world recruited in this season has no meta
// row yet, so it joins the designer when the season report files it
// (R1242).
//
// The Fleet Designer (monetization.md §3.2, R1249, R1252) is `fleetcard.ts`:
// the bodies of FLEET NAMES and FLEET LIVERY, both behind the
// `fleet_designer` key. The panel hands it every action that starts with
// `fd-`.
//
// What is honestly empty today:
//   - A new account has no captain at all. A world files its captains back
//     in its season report, so an empty roster is the normal first state
//     and the card says when its captains come.
//   - Cryo Freeze (R1184) has no control. It is a rule of the meta server,
//     so the card states the rule and whether the key is held.
//   - There is no berth count on the wire. A roster with a captain draws
//     `CAPTAIN_SLOTS_MAX` slots at least, the most captains one world
//     fields, and a free slot shows a placeholder (R1547). An empty roster
//     draws one row of three placeholders, so a first visit reads one
//     quiet row and not a wall of empty slots (R1734).

import { oneOf } from '../../../util/oneof';
import { runeCount } from '../../../util/text';
import { button, pillGroup, previewTiles, tabStrip } from '../../choice/choice';
import {
  ApiError, KEY_CAPTAIN_DESIGNER, KEY_PREMIUM, KEY_SABBATICAL, deleteCaptainLooks, getWardroom,
  messageOf, putCaptainLooks,
  type Wardroom, type WardroomCaptain,
} from '../../../net/api';
import { TTL_ON_OPEN, hasKey, hubFaction, type Cell } from '../hubstate';
import { CHROME_ARM_MS, HUB_SECTIONS, PORTRAIT } from '../../../gen/presentation';
import {
  ATTR_TRAIN_CAP, CAPTAIN_BACKSTORY_MAX_RUNES, CAPTAIN_NAME_MAX_RUNES, CAPTAIN_SLOTS_MAX,
  CAPTAIN_VOICE_LINE_MAX_RUNES, CAPTAIN_VOICE_MAX_LINES, CODE_INVALID_CAPTAIN_LOOKS,
} from '../../../gen/balance';
import { factionColor } from '../../../world';
import { ArmGate } from '../../arm';
import { portraitHtml } from '../../portrait/portraits';
import { GENDER_FEMALE, GENDER_MALE, phaseOfAge, storedLooks, type Phase } from '../../portrait/seed';
import { hubWardroom as S, overlay as O } from '../../../gen/strings';
import {
  armedButton, armedPress, empty, esc, h1, lede, pending, ctlRow, plain, tierLine, tierTag, wireOnce,
  type EmptyPress, type Panel, type PanelContext,
} from '../panel';
import { SectionNav, shellHtml, shellPress, type ShellPage, type ShellSection } from '../sectionshell';
import {
  bodyOf, candidateSeeds, changed, draftOf, precheck, refusalField,
  voiceLines, type LooksDraft, type LooksField,
} from './designer';
import {
  FleetDesignerCard, counter, fieldHead, fleetLiveryCell, fleetNamesCell, formErr, formNote, liveryName, shopPress,
  showCount,
} from './fleetcard';
import { namingDiagram } from './fleetdiagram';
import { asPick } from './liverypick';
import { liveryPreviewUrl } from './liverypreview';

/** The three rosters the panel reads. A clanwar volume of the chronicle
 *  reads the clanwar roster too (R1244). */
type Mode = 'core' | 'event' | 'clanwar';

/** The rosters in the order of the toggle row and of the strip. */
const MODES: readonly Mode[] = ['core', 'event', 'clanwar'];

/** The five sections of WARDROOM, in the rail order (§16.9). The ids are
 *  the address words: `?tab=wardroom&section=designer`. */
export const WARDROOM_SECTIONS = ['rosters', 'cryo', 'designer', 'names', 'livery'] as const;
export type WardroomSection = (typeof WARDROOM_SECTIONS)[number];

/** The other words an address may use. A roster mode opens ROSTERS on
 *  that roster, as an HONOUR toggle word of RANKS does. The words of the
 *  one FLEET DESIGNER page before R2186 open FLEET NAMES. */
export const WARDROOM_ALIASES: Readonly<Record<string, WardroomSection>> = {
  core: 'rosters', event: 'rosters', clanwar: 'rosters', captains: 'rosters',
  'captain-designer': 'designer', fleet: 'names', 'fleet-designer': 'names',
  'fleet-names': 'names', 'fleet-livery': 'livery',
};

/** The press of a rail row and of a tile, and ‹ WARDROOM of a narrow page. */
const GO = 'wd-sec';
const BACK = 'wd-back';
const SHELL = { go: GO, back: BACK };

/** The word of each career phase in the dossier. */
const PHASE_WORD: Record<Phase, string> = {
  rise: S.phase_rise, peak: S.phase_peak, decline: S.phase_decline,
};

/**
 * The summary of ROSTERS: the captain count of each roster whose read has
 * answered, in the toggle order. A roster that has not answered names no
 * count, so the summary never states a figure the data lacks (§16.16).
 */
export function rosterSummary(counts: Readonly<Partial<Record<Mode, number>>>): string {
  const word = { core: S.sum_core, event: S.sum_event, clanwar: S.sum_clanwar };
  return MODES.flatMap((m) => {
    const n = counts[m];
    return n === undefined ? [] : [word[m]({ n })];
  }).join(' · ');
}

/**
 * The dossier of one captain (card 05, R1118): the portrait at
 * `wardroom_dossier_px`, the name, the phase with the age and the seasons
 * served, the talents, and one bar for each attribute to `ATTR_TRAIN_CAP`. A
 * talent is the hero ink with an underline, as the captain row draws it
 * (R1889). `press` is markup under the bars.
 */
function dossierHtml(c: WardroomCaptain, faction: number, press = ''): string {
  const px = HUB_SECTIONS.wardroomDossierPx;
  const talents = c.talents.map((t) => O.attr_short[t] ?? '?').join(' · ');
  const line = dossierLine(c);
  let abil = '';
  for (let i = 0; i < c.attrs.length; i++) {
    const v = c.attrs[i] ?? 0;
    const share = ATTR_TRAIN_CAP > 0 ? Math.min(1, v / ATTR_TRAIN_CAP) : 0;
    abil += `<span class="wd-an${c.talents.includes(i) ? ' t' : ''}">${esc(O.attr_short[i] ?? '')}</span>`
      + `<span class="wd-bar"><i style="width:${Math.round(share * 100)}%"></i></span>`
      + `<span class="wd-av">${esc(S.dossier_value({ v, cap: ATTR_TRAIN_CAP }))}</span>`;
  }
  return `<div class="wd-dhead"><div class="wd-face" style="--wd-px:${px}px">${face(c, faction, px, 'p')}</div>`
    + `<div class="wd-dt"><b>${esc(c.name)}</b><span>${esc(line)}</span>`
    + (talents === '' ? '' : `<span class="wd-tal">${esc(S.dossier_talents({ list: talents }))}</span>`)
    + '</div></div>'
    + (abil === '' ? '' : `<div class="wd-abil">${abil}</div>`)
    + press;
}

/** The line under the name of a captain in the dossier and in the
 *  preview of the designer: the phase, the age and the seasons served. A
 *  blitz world ages a captain by a fraction, and a world plays with the
 *  figure rounded down, so the line does too. */
function dossierLine(c: WardroomCaptain): string {
  return S.dossier_line({
    phase: PHASE_WORD[phaseOfAge(c.age)], age: ageOf(c), seasons: Math.floor(c.seasonsServed),
  });
}

/** The cache key of one roster read. The chronicle book reads the cell of
 *  the roster of its volume for the voice lines (chronicle.md §4.3, R1244),
 *  so the book and the panel share one fetch per roster. */
function wardroomKey(mode: Mode): string {
  return `wardroom:${mode}`;
}

/** One roster, through the hub cache. */
export function wardroomCell(ctx: Pick<PanelContext, 'data'>, mode: Mode): Cell<Wardroom> {
  return ctx.data.cell<Wardroom>(wardroomKey(mode), TTL_ON_OPEN, () => getWardroom(mode));
}

/** The markup of one portrait of a captain in a box of class `cls`. */
function face(c: WardroomCaptain, faction: number, px: number, cls: string): string {
  return portraitHtml({
    name: c.name, talents: c.talents, phase: phaseOfAge(c.age), faction,
    ...storedLooks(c.portraitSeed, c.portraitGender),
  }, px, cls);
}

/**
 * The age of one captain in whole years (numbers.md §12, R1170). The meta
 * server computes it, so the tile mirrors no rule, and every season adds
 * whole years, so the figure is always whole.
 */
function ageOf(c: WardroomCaptain): string {
  return Number.isFinite(c.age) ? String(c.age) : '—';
}

/** The attributes of a press that picks one captain of the roster `mode`
 *  for the dossier and the designer: a roster tile and a strip face. */
function pickAttrs(mode: Mode, id: number): string {
  return ` data-act="wardroom-pick" data-mode="${mode}" data-id="${id}" role="button" tabindex="0"`;
}

/** What makes a roster tile a press: the mode it stands in and the
 *  captain id the dossier shows, 0 for none. Undefined for the tiles of an
 *  empty roster, which are no controls. */
interface TilePick { mode: Mode; on: number }

/** The columns of a roster grid (hub.css `.caps`), and so the placeholder
 *  slots of an empty roster: one row (R1734). */
const CAPS_COLUMNS = 3;

/** The tile grid of one roster. `faction` is the banner the portrait's
 *  faction dot wears, 0 for none. A roster with a captain holds
 *  `CAPTAIN_SLOTS_MAX` slots at least, three to a row, and a slot with no
 *  captain shows a placeholder (hub.css `.caps`, R1547). An empty roster
 *  holds one row of placeholders (R1734); the card grid still stretches
 *  the cards of one row to one height. With `pick`, a tile of a captain
 *  with a game id picks it for the dossier and the Captain Designer. */
function capTiles(list: WardroomCaptain[], faction: number, pick?: TilePick): string {
  const slots = list.length === 0 ? CAPS_COLUMNS : CAPTAIN_SLOTS_MAX;
  const free = Math.max(0, slots - list.length);
  return `<div class="caps">${list.map((c) => {
    const press = pick !== undefined && c.id > 0;
    const attrs = press ? pickAttrs(pick.mode, c.id) : '';
    const cls = `cap${press ? ' press' : ''}${press && pick.on === c.id ? ' on' : ''}`;
    return `<div class="${cls}"${attrs}>`
      + face(c, faction, PORTRAIT.tilePx, 'p')
      + `<div class="n">${esc(c.name)}</div>`
      + `<div class="a">${S.age({ n: esc(ageOf(c)) })}</div></div>`;
  }).join('')}${SLOT.repeat(free)}</div>`;
}

/** One free slot of a roster: the outline of a tile with no captain. The
 *  two empty lines keep the height of a tile. */
const SLOT = '<div class="cap slot" aria-hidden="true"><div class="p"></div>'
  + '<div class="n">&nbsp;</div><div class="a">&nbsp;</div></div>';

/** The roster mode a tile names, core for an unknown word. */
function modeOf(word: string | undefined): Mode {
  return oneOf(MODES, word) ?? 'core';
}

/** The WARDROOM panel. */
class WardroomPanel implements Panel {
  readonly key = 'wardroom' as const;

  /** The picked captain, by the mode of its roster: the DOSSIER shows it,
   *  and the Captain Designer edits it. */
  private sel?: { mode: Mode; id: number };
  /** The draft of the open captain. */
  private draft?: LooksDraft;
  /** The seeds of the portrait grid, the current face first. */
  private faces: number[] = [];
  /** The call in flight, if any. */
  private busy: '' | 'save' | 'reset' = '';
  /** The last refusal: the field it names, '' for the card as a whole. */
  private err?: { field: LooksField | ''; text: string };
  /** The plain outcome of the last call. */
  private notice = '';
  /** The two-press gate of RESET TO DEFAULT (`ui/arm.ts`, R837). */
  private gate = new ArmGate(CHROME_ARM_MS, () => this.ctx.refresh());
  /** The Fleet Designer card (`fleetcard.ts`). */
  private fleet: FleetDesignerCard;
  /** The open section and the section the address named (§16.16). */
  private nav = new SectionNav({
    ids: () => WARDROOM_SECTIONS, first: () => 'rosters', aliases: WARDROOM_ALIASES,
  });
  /** The roster the toggle row of ROSTERS shows. */
  private mode: Mode = 'core';
  /** The roster an address word named, for the next `open`. */
  private askedMode?: Mode;
  /** True while the dossier stands as the sub-page of ROSTERS on a
   *  narrow pane (R1919). */
  private sheet = false;

  constructor(private ctx: PanelContext) {
    this.fleet = new FleetDesignerCard(ctx);
  }

  open(): void {
    this.nav.open();
    this.mode = this.askedMode ?? 'core';
    this.askedMode = undefined;
    this.sheet = false;
  }

  openSection(word: string): void {
    if (!this.nav.ask(word)) return;
    const mode = oneOf(MODES, word);
    if (mode === undefined) return;
    // While WARDROOM stands open the word acts now, as the section does
    // (R2237); else the next `open` takes it.
    if (!this.nav.isOpen) { this.askedMode = mode; return; }
    this.mode = mode;
    this.sheet = false;
  }

  close(): void {
    this.gate.clear(false);
    this.fleet.clear();
    this.nav.close();
    this.sheet = false;
  }

  destroy(): void {
    this.close();
  }

  /** True while the account holds the designer key. */
  private designing(): boolean {
    return hasKey(this.ctx.me(), KEY_CAPTAIN_DESIGNER);
  }

  html(): string {
    const wide = this.ctx.wide();
    const open = this.nav.shown(wide);
    return shellHtml({
      title: S.title,
      heading: h1(S.title, S.title_sub),
      go: GO,
      back: BACK,
      sections: this.sections(),
      open,
      wide,
      // The tiles of a narrow pane draw no page.
      page: open === null ? { body: '' } : this.sectionPage(open, wide),
      rootHead: this.strip(HUB_SECTIONS.wardroomStripPortraits),
    });
  }

  // --- the section shell (§16.9, §16.16) -------------------------------------

  /** The five sections as the rail and the tiles draw them. */
  private sections(): ShellSection[] {
    const counts: Partial<Record<Mode, number>> = {};
    for (const m of MODES) {
      const v = wardroomCell(this.ctx, m).value;
      if (v !== undefined) counts[m] = v.captains.length;
    }
    return [
      { id: 'rosters', glyph: '⚓', name: S.sec_rosters, sum: rosterSummary(counts) },
      { id: 'cryo', glyph: '❄', name: S.card_cryo, sum: this.tierSum('premium', KEY_SABBATICAL) },
      { id: 'designer', glyph: '✎', name: S.card_designer, sum: this.designerSum() },
      { id: 'names', glyph: '№', name: S.card_fleet_names, sum: this.namesSum() },
      { id: 'livery', glyph: '▲', name: S.card_fleet_livery, sum: this.liverySum() },
    ];
  }

  /** The summary of a gated section: the line of its tier tag (R2046). */
  private tierSum(tier: 'supporter' | 'premium', key: string): string {
    const me = this.ctx.me();
    return tierLine(tier, hasKey(me, key), me);
  }

  /** The tier tag of a gated page, in its page title (R1945). */
  private keyTag(tier: 'supporter' | 'premium', key: string): string {
    const me = this.ctx.me();
    return tierTag(tier, hasKey(me, key), me);
  }

  /** The CAPTAIN DESIGNER summary: the name of the open captain, else the
   *  tier and whether it is held. */
  private designerSum(): string {
    const c = this.designing() && this.draft !== undefined ? this.selected() : undefined;
    return c === undefined ? this.tierSum('supporter', KEY_CAPTAIN_DESIGNER) : c.name;
  }

  /** The FLEET NAMES summary: the count of the saved pool, once its read
   *  answered. */
  private namesSum(): string {
    const names = fleetNamesCell(this.ctx).value;
    return names === undefined ? '' : S.sum_names({ n: names.length });
  }

  /** The FLEET LIVERY summary: the saved livery, once its read answered. */
  private liverySum(): string {
    const code = fleetLiveryCell(this.ctx).value;
    if (code === undefined) return '';
    const pick = asPick(code);
    return pick === '' ? S.livery_off : liveryName(pick);
  }

  /** The page of one section. */
  private sectionPage(id: WardroomSection, wide: boolean): ShellPage {
    const dossier = { label: S.dossier_label, html: this.dossier() };
    switch (id) {
      case 'rosters': {
        // Under the room for the column the dossier follows the roster; a
        // narrow pane opens it as a sub-page on a press instead (R1919).
        const c = wide || !this.sheet ? undefined : this.dossierCaptain();
        if (c !== undefined) {
          return {
            body: plain(this.dossier()),
            sub: { name: c.name, back: 'wd-dossier-back' },
          };
        }
        // The page shows one roster, so its tier tag stands in the page
        // title (R1945).
        return {
          body: this.toggleRow() + this.rosterCard(this.mode),
          mark: this.mode === 'event' ? this.keyTag('premium', KEY_PREMIUM) : undefined,
          companion: { ...dossier, fold: 'below' },
        };
      }
      case 'cryo':
        return {
          body: this.cryoCard(),
          mark: this.keyTag('premium', KEY_SABBATICAL),
          companion: { ...dossier, fold: 'none' },
        };
      case 'designer': {
        const preview = this.previewHtml();
        return {
          body: this.designerCard(),
          mark: this.keyTag('supporter', KEY_CAPTAIN_DESIGNER),
          companion: preview === '' ? undefined
            : { label: S.preview_label, html: preview, fold: 'top', narrow: 'top' },
        };
      }
      case 'names':
        return {
          body: this.fleet.namesHtml(),
          mark: this.fleet.mark(),
          companion: {
            label: S.names_side_label, html: this.namesSide(false), folded: this.namesSide(true),
            fold: 'top', narrow: 'top',
          },
        };
      case 'livery':
        return {
          body: this.fleet.liveryHtml(),
          mark: this.fleet.mark(),
          companion: {
            label: S.fleet_side_label, html: this.liverySide(false), folded: this.liverySide(true),
            fold: 'top', narrow: 'top',
          },
        };
    }
  }

  /** The tab strip of ROSTERS: one tab a roster, with its count once its
   *  read answered (the HONOUR tab strip of RANKS, R1737, R2011). */
  private toggleRow(): string {
    const name = { core: S.toggle_core, event: S.toggle_event, clanwar: S.toggle_clanwar };
    const tabs = MODES.map((m) => ({ id: m, label: name[m], n: wardroomCell(this.ctx, m).value?.captains.length }));
    return ctlRow(tabStrip(tabs, this.mode, 'wd-mode', { label: S.toggle_label }));
  }

  /** The body of one roster: the page shows one roster at a time, so it
   *  draws no box and no title; the toggle row names it (R1945). */
  private rosterCard(mode: Mode): string {
    const body = mode === 'core' ? this.coreCard() : mode === 'event' ? this.eventCard() : this.clanwarCard();
    return `<div class="wd-roster">${body}</div>`;
  }

  /** The captain the dossier shows: the picked one where it stands on the
   *  roster the toggle shows, else the first captain of that roster. */
  private dossierCaptain(): WardroomCaptain | undefined {
    const list = wardroomCell(this.ctx, this.mode).value?.captains ?? [];
    const sel = this.sel;
    const picked = sel !== undefined && sel.mode === this.mode
      ? list.find((c) => c.id === sel.id) : undefined;
    return picked ?? list[0];
  }

  /** The DOSSIER, with ▸ CAPTAIN DESIGNER for a key holder. */
  private dossier(): string {
    const cell = wardroomCell(this.ctx, this.mode);
    if (cell.value === undefined) return pending(cell);
    const c = this.dossierCaptain();
    if (c === undefined) return lede(S.dossier_empty);
    const press = this.designing() && c.id > 0
      ? ctlRow(button(S.to_designer, { act: 'wd-design', data: { mode: this.mode, id: String(c.id) } }, { variant: 'primary' }))
      : '';
    return dossierHtml(c, hubFaction(this.ctx.me()), press);
  }

  /**
   * A strip of portraits of the rosters in the toggle order, at most
   * `limit`, with the count of the rest. A portrait of a captain with a
   * game id is a press that picks the captain. The strip stands over the
   * tiles of a telephone and over the form of the Captain Designer.
   */
  private strip(limit: number): string {
    const all = MODES.flatMap((mode) =>
      (wardroomCell(this.ctx, mode).value?.captains ?? []).map((c) => ({ mode, c })));
    if (all.length === 0) return '';
    const faction = hubFaction(this.ctx.me());
    const px = PORTRAIT.rowPx;
    const shown = all.slice(0, limit);
    const more = all.length - shown.length;
    return '<div class="wd-strip">' + shown.map(({ mode, c }) => {
      const on = this.sel?.mode === mode && this.sel.id === c.id;
      const attrs = c.id > 0
        ? `${pickAttrs(mode, c.id)} aria-label="${esc(c.name)}"${on ? ' aria-pressed="true"' : ''}`
        : '';
      return `<div class="wd-sf${on ? ' on' : ''}" style="--wd-px:${px}px"${attrs}>${face(c, faction, px, 'p')}</div>`;
    }).join('') + (more > 0 ? `<span class="wd-more">${esc(S.strip_more({ n: more }))}</span>` : '') + '</div>';
  }

  /** The live preview of the Captain Designer: the face the draft picked,
   *  its name and its phase, and REROLL. Empty while no captain is open. */
  private previewHtml(): string {
    const c = this.selected();
    const d = this.draft;
    if (!this.designing() || c === undefined || d === undefined) return '';
    const px = HUB_SECTIONS.wardroomDossierPx;
    const look = portraitHtml({
      name: c.name, talents: c.talents, phase: phaseOfAge(c.age), faction: hubFaction(this.ctx.me()),
      seed: d.seed, female: d.gender === GENDER_FEMALE,
    }, px, 'p');
    return `<div class="wd-prev"><div class="wd-face" style="--wd-px:${px}px">${look}</div>`
      + `<b>${esc(d.name.trim() === '' ? c.name : d.name.trim())}</b><span>${esc(dossierLine(c))}</span></div>`
      + ctlRow(button(S.reroll, { act: 'cd-reroll' }, { variant: 'primary', disabled: this.busy !== '' }));
  }

  /** The names the hulls take: the draft of the card, else the saved
   *  pool, else undefined before the read answered. */
  private poolNames(): string[] | undefined {
    return this.fleet.draftLook().names ?? fleetNamesCell(this.ctx).value;
  }

  /** The naming diagram of FLEET NAMES (R2186): the reader's faction
   *  letter and the first names of the draft pool. `short` is the form of a
   *  fold. */
  private namesSide(short: boolean): string {
    const names = this.poolNames();
    if (names === undefined) return pending(fleetNamesCell(this.ctx));
    return namingDiagram(hubFaction(this.ctx.me()), names, short);
  }

  /** What the hulls wear: the livery on one hull in the faction ink, the
   *  first name, and the livery name. The draft of the card wins over the
   *  saved pick and pool, so the column follows the form before a SAVE
   *  (R1912). The column bakes the hull at `livery_side_scale`; the fold
   *  `short` draws it smaller (R2186). */
  private liverySide(short: boolean): string {
    const lv = fleetLiveryCell(this.ctx);
    if (lv.value === undefined) return pending(lv);
    const pick = this.fleet.draftLook().livery ?? asPick(lv.value);
    const url = liveryPreviewUrl(pick, factionColor(hubFaction(this.ctx.me()) || 1), HUB_SECTIONS.liverySideScale);
    const first = this.poolNames()?.[0];
    return (url === '' ? '' : `<img class="wd-hull${short ? ' short' : ''}" src="${url}" alt="">`)
      + (first === undefined ? '' : `<b class="wd-plate">${esc(first)}</b>`)
      + `<span class="wd-pl">${esc(pick === '' ? S.livery_off : S.fleet_side_livery({ name: liveryName(pick) }))}</span>`;
  }

  /** Opens one section. */
  private goTo(id: WardroomSection): void {
    if (!this.nav.go(id, this.ctx.wide())) return;
    this.sheet = false;
    this.ctx.play('sfx-menu-tab');
    this.ctx.refresh();
  }

  /**
   * One roster read as its block: the tile grid, and under an empty grid
   * the lines `what` and `why`, and the one forward press where one is
   * given (R1742). A tile of a captain with a game id picks the captain
   * for the dossier and the designer.
   */
  private roster(mode: Mode, what: string, why: string, press?: EmptyPress): string {
    const cell = wardroomCell(this.ctx, mode);
    if (cell.value === undefined) return pending(cell);
    const list = cell.value.captains;
    const faction = hubFaction(this.ctx.me());
    if (list.length === 0) return capTiles(list, faction) + empty(what, why, press);
    const shown = mode === this.mode ? this.dossierCaptain() : undefined;
    return capTiles(list, faction, { mode, on: shown?.id ?? 0 });
  }

  /** The core roster. Every account holds one, and a new account holds an
   *  empty one. */
  private coreCard(): string {
    // A captain comes from a season that closes, so the one press of the
    // empty section leads to PLAY. The other rosters fill the same way
    // and repeat no press (R1742).
    const r = this.roster('core', S.core_empty, S.core_empty_why,
      { label: S.core_empty_press, tab: 'play' });
    return plain(r + lede(S.core_lede));
  }

  /**
   * The event roster, behind the premium key. The tag (in the page title,
   * R1945) and the prose stay in
   * full colour whether the key is held or not, because a reader must be
   * able to read what the mode is; only the roster block fades.
   */
  private eventCard(): string {
    const held = hasKey(this.ctx.me(), KEY_PREMIUM);
    const r = this.roster('event', S.event_empty,
      held ? S.event_empty_held : S.event_empty_unheld);
    return plain(`<div${held ? '' : ' class="faded"'}>${r}</div>`
      + lede(S.event_lede));
  }

  /**
   * The clanwar roster (R745, R1532). Its toggle always stands, and an
   * empty roster shows its empty state.
   */
  private clanwarCard(): string {
    const r = this.roster('clanwar', S.clanwar_empty, S.clanwar_empty_why);
    return plain(r + lede(S.clanwar_lede));
  }

  /**
   * Cryo Freeze, the Premium perk the `sabbatical` key carries (R1184,
   * monetization.md §4). A captain does not age in a season that its
   * player did not play at all.
   */
  private cryoCard(): string {
    const held = hasKey(this.ctx.me(), KEY_SABBATICAL);
    return plain((held ? '' : lede(S.cryo_unheld))
      + lede(S.cryo_lede)
      + (held ? '' : ctlRow(shopPress())));
  }

  /**
   * The Captain Designer (monetization.md §3.1). Without the key, the card
   * names the tier and points at the shop. With it, the card holds the
   * strip of every captain, and edits the captain a tile or the strip
   * picked.
   */
  private designerCard(): string {
    const held = this.designing();
    const head = lede(S.designer_lede);
    if (!held) {
      return plain(head + ctlRow(shopPress()));
    }
    const strip = this.strip(Number.POSITIVE_INFINITY);
    const c = this.selected();
    if (c === undefined || this.draft === undefined) {
      return plain(head + strip + empty(S.designer_pick, strip === '' ? S.designer_pick_why : S.designer_pick_strip));
    }
    return plain(head + strip + this.form(c, this.draft), 'cdcard');
  }

  /** One roster as the cache holds it now, or undefined before its read
   *  landed. A press acts on what the pane drew, so it peeks. */
  private cached(mode: Mode): Wardroom | undefined {
    return this.ctx.data.peek<Wardroom>(wardroomKey(mode)).value;
  }

  /** One captain of a cached roster, by its game id. */
  private captain(mode: Mode, id: number): WardroomCaptain | undefined {
    return this.cached(mode)?.captains.find((c) => c.id === id);
  }

  /** The open captain as the roster read last answered it, or undefined. */
  private selected(): WardroomCaptain | undefined {
    const sel = this.sel;
    return sel === undefined ? undefined : this.captain(sel.mode, sel.id);
  }

  /** The refusal line of one field, or ''. */
  private errLine(field: LooksField | ''): string {
    return this.err !== undefined && this.err.field === field ? formErr(this.err.text) : '';
  }

  /** The form of one captain in two columns: the name, the portrait and
   *  the backstory on the left, the voice lines and the presses on the
   *  right. A narrow pane stacks the two (ui-map-interface.md §16.9,
   *  R1547). */
  private form(c: WardroomCaptain, d: LooksDraft): string {
    const faction = hubFaction(this.ctx.me());
    const female = d.gender === GENDER_FEMALE;
    // The faces differ by look alone, so they are preview tiles (R2011).
    const grid = previewTiles(this.faces.map((seed, i) => ({
      id: String(seed), label: S.face_label({ n: i + 1 }),
      picture: `<span class="cdpic" aria-hidden="true">${portraitHtml({
        name: c.name, talents: c.talents, phase: phaseOfAge(c.age), faction, seed, female,
      }, PORTRAIT.rowPx, 'p')}</span>`,
    })), String(d.seed), 'cd-face', { label: S.label_portrait });
    const voice = d.voice.map((line, i) => '<div class="cdv">'
      + `<input data-in="cd-voice-${i}" type="text" autocomplete="off" `
      + `maxlength="${CAPTAIN_VOICE_LINE_MAX_RUNES * 2}" `
      + `placeholder="${esc(S.voice_placeholder({ n: i + 1 }))}" value="${esc(line)}">`
      + counter(`cd-voice-${i}`, runeCount(line.trim()), CAPTAIN_VOICE_LINE_MAX_RUNES)
      + '</div>').join('');
    const busy = this.busy !== '';
    const save = button(this.busy === 'save' ? S.saving : S.save, { act: 'cd-save' },
      { variant: 'primary', disabled: busy });
    const reset = armedButton(this.gate, 'cd-reset', S.reset, S.reset_armed, busy ? null : { act: 'cd-reset' });
    // The gender of the portrait is one saved value of the draft (R2011).
    const gender = pillGroup([
      { id: String(GENDER_FEMALE), label: S.gender_f, title: S.gender_f_title },
      { id: String(GENDER_MALE), label: S.gender_m, title: S.gender_m_title },
    ], String(d.gender), 'cd-gender', { label: S.label_portrait });
    return '<div class="cdsplit"><div class="cd">'
      + fieldHead(S.label_name, counter('cd-name', runeCount(d.name.trim()), CAPTAIN_NAME_MAX_RUNES))
      + `<input data-in="cd-name" type="text" autocomplete="off" maxlength="${CAPTAIN_NAME_MAX_RUNES * 2}" `
      + `aria-label="${esc(S.label_name)}" value="${esc(d.name)}">`
      + this.errLine('name')
      + lede(S.name_hint)
      + fieldHead(S.label_portrait)
      + ctlRow(button(S.reroll, { act: 'cd-reroll' }, { variant: 'primary', disabled: busy }) + gender)
      + grid
      + this.errLine('portraitGender')
      + fieldHead(S.label_backstory,
        counter('cd-story', runeCount(d.backstory.trim()), CAPTAIN_BACKSTORY_MAX_RUNES))
      + `<textarea data-in="cd-story" rows="5" maxlength="${CAPTAIN_BACKSTORY_MAX_RUNES * 2}" `
      + `aria-label="${esc(S.label_backstory)}">${esc(d.backstory)}</textarea>`
      + this.errLine('backstory')
      + '</div><div class="cd">'
      + fieldHead(S.label_voice, '<span class="cdc" data-count="cd-voice">'
        + `${esc(S.voice_count({ n: voiceLines(d).length, max: CAPTAIN_VOICE_MAX_LINES }))}</span>`)
      + voice
      + this.errLine('voice')
      + lede(S.private_hint)
      + ctlRow(save + reset + button(S.designer_close, { act: 'cd-close' }))
      + this.errLine('')
      + formNote(this.notice)
      + '</div></div>';
  }

  act(action: string, el: HTMLElement): void {
    if (action.startsWith('fd-')) {
      this.fleet.act(action, el);
      return;
    }
    if (shellPress(this.nav, this.ctx, SHELL, action, el, () => { this.sheet = false; })) return;
    switch (action) {
      case 'wd-mode':
        this.ctx.play('sfx-menu-toggle');
        this.mode = modeOf(el.dataset.id);
        this.sheet = false;
        this.ctx.refresh();
        return;
      case 'wd-dossier-back':
        // ‹ ROSTERS: back to the roster at the place the reader left.
        this.ctx.play('sfx-menu-back');
        this.nav.leave();
        this.sheet = false;
        this.ctx.refresh();
        return;
      case 'wd-design':
        // ▸ CAPTAIN DESIGNER of the dossier: the designer opens on the
        // captain of the dossier.
        this.pick(modeOf(el.dataset.mode), Number(el.dataset.id ?? 0));
        this.goTo('designer');
        return;
      case 'wardroom-shop':
        this.ctx.play('sfx-menu-press');
        this.ctx.go('shop');
        return;
      case 'wardroom-pick':
        this.pick(modeOf(el.dataset.mode), Number(el.dataset.id ?? 0));
        return;
      case 'cd-face': {
        const seed = Number(el.dataset.id ?? 0);
        if (this.draft === undefined || !(seed > 0)) return;
        this.ctx.play('sfx-menu-toggle');
        this.draft.seed = seed;
        this.notice = '';
        this.ctx.refresh();
        return;
      }
      case 'cd-reroll':
        if (this.draft === undefined) return;
        this.ctx.play('sfx-menu-press');
        // The face the draft holds stays first, so a reroll never loses it.
        this.faces = candidateSeeds(this.draft.seed, PORTRAIT.candidates);
        this.ctx.refresh();
        return;
      case 'cd-gender': {
        const g = Number(el.dataset.id);
        if (this.draft === undefined || (g !== GENDER_MALE && g !== GENDER_FEMALE)) return;
        this.ctx.play('sfx-menu-toggle');
        this.draft.gender = g;
        this.notice = '';
        this.ctx.refresh();
        return;
      }
      case 'cd-save':
        this.save();
        return;
      case 'cd-reset':
        if (!armedPress(this.ctx, this.gate, 'cd-reset')) return;
        this.reset();
        return;
      case 'cd-close':
        this.ctx.play('sfx-menu-back');
        this.gate.clear(false);
        this.sel = undefined;
        this.draft = undefined;
        this.err = undefined;
        this.notice = '';
        this.ctx.refresh();
        return;
      default:
    }
  }

  /**
   * Picks one captain: the dossier shows it, the toggle shows its roster,
   * and with the designer key the designer opens its draft. A second tap
   * on the picked captain keeps the draft. A press on the strip of the
   * tiles of a telephone opens ROSTERS; a press on a tile of a narrow pane
   * opens the dossier as the sub-page of ROSTERS (R1919).
   */
  private pick(mode: Mode, id: number): void {
    if (!(id > 0)) return;
    const c = this.captain(mode, id);
    if (c === undefined) return;
    const wide = this.ctx.wide();
    const same = this.sel?.mode === mode && this.sel.id === id;
    this.ctx.play('sfx-menu-press');
    this.mode = mode;
    const fromRoot = this.nav.shown(wide) === null;
    if (fromRoot) this.nav.go('rosters', wide);
    if (!wide && !this.sheet) this.nav.enter(fromRoot ? 0 : undefined);
    this.sheet = !wide;
    if (!same || (this.designing() && this.draft === undefined)) {
      this.gate.clear(false);
      this.sel = { mode, id };
      if (this.designing()) this.load(c);
      else this.draft = undefined;
    }
    this.ctx.refresh();
  }

  /** Takes the server's captain as the new draft, with a fresh grid. */
  private load(c: WardroomCaptain): void {
    const d = draftOf(c);
    this.draft = d;
    this.faces = candidateSeeds(d.seed, PORTRAIT.candidates);
    this.err = undefined;
    this.notice = '';
  }

  /** The roster name of one mode, as the read answered it. */
  private rosterOf(mode: Mode): string {
    return this.cached(mode)?.roster ?? '';
  }

  /** SAVE: one press. The rune counts are checked here first; every other
   *  rule is the server's. */
  private save(): void {
    const sel = this.sel;
    const d = this.draft;
    const c = this.selected();
    if (sel === undefined || d === undefined || c === undefined || this.busy !== '') return;
    const bad = precheck(d);
    if (bad !== null) {
      this.ctx.play('sfx-menu-error');
      this.err = {
        field: bad.field,
        text: bad.reason === 'short' ? S.too_short({ limit: bad.limit }) : S.too_long({ limit: bad.limit }),
      };
      this.notice = '';
      this.ctx.refresh();
      return;
    }
    if (!changed(d, c)) {
      this.err = undefined;
      this.notice = S.saved;
      this.ctx.refresh();
      return;
    }
    const body = bodyOf(d);
    this.run('save', sel, () => putCaptainLooks(this.rosterOf(sel.mode), sel.id, body), S.saved);
  }

  /** RESET TO DEFAULT, after the second press. The route needs no key
   *  (R1241); the card shows the control to a key holder only. */
  private reset(): void {
    const sel = this.sel;
    if (sel === undefined || this.busy !== '') return;
    this.run('reset', sel, () => deleteCaptainLooks(this.rosterOf(sel.mode), sel.id), S.reset_done);
  }

  /** One call of the designer. The answer is the captain, which becomes
   *  the draft; the roster cell refreshes behind it. */
  private run(
    kind: 'save' | 'reset', sel: { mode: Mode; id: number },
    call: () => Promise<WardroomCaptain>, done: string,
  ): void {
    this.busy = kind;
    this.err = undefined;
    this.notice = '';
    this.ctx.play('sfx-menu-press');
    this.ctx.refresh();
    call().then((c) => {
      this.busy = '';
      if (this.sel?.mode === sel.mode && this.sel.id === sel.id) {
        this.load(c);
        this.notice = done;
      }
      this.ctx.data.expire(wardroomKey(sel.mode));
      this.ctx.refresh();
    }, (err: unknown) => {
      this.busy = '';
      this.ctx.play('sfx-menu-error');
      const field = err instanceof ApiError && err.code === CODE_INVALID_CAPTAIN_LOOKS
        ? refusalField(err.field) : null;
      this.err = { field: field ?? '', text: messageOf(err) };
      this.ctx.refresh();
    });
  }

  /**
   * The boxes. The text lives in the draft, and the markup carries it back
   * as a value, because the pane is rewritten whenever the markup changes.
   * A counter moves at once, without a rewrite.
   */
  wire(root: HTMLElement): void {
    this.nav.scroll(root);
    this.fleet.wire(root);
    root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-in^="cd-"]').forEach((box) => {
      if (!wireOnce(box)) return;
      const name = box.dataset.in ?? '';
      box.addEventListener('input', () => this.typed(root, name, box.value));
    });
  }

  /** One keystroke in one box of the form. */
  private typed(root: HTMLElement, name: string, value: string): void {
    const d = this.draft;
    if (d === undefined) return;
    let max: number;
    if (name === 'cd-name') {
      d.name = value;
      max = CAPTAIN_NAME_MAX_RUNES;
    } else if (name === 'cd-story') {
      d.backstory = value;
      max = CAPTAIN_BACKSTORY_MAX_RUNES;
    } else if (name.startsWith('cd-voice-')) {
      const i = Number(name.slice('cd-voice-'.length));
      if (!(i >= 0 && i < d.voice.length)) return;
      d.voice[i] = value;
      max = CAPTAIN_VOICE_LINE_MAX_RUNES;
      const all = root.querySelector<HTMLElement>('[data-count="cd-voice"]');
      if (all) all.textContent = S.voice_count({ n: voiceLines(d).length, max: CAPTAIN_VOICE_MAX_LINES });
    } else {
      return;
    }
    this.notice = '';
    showCount(root, name, runeCount(value.trim()), max);
  }
}

/** Makes the WARDROOM panel. */
export function createWardroomPanel(ctx: PanelContext): Panel {
  return new WardroomPanel(ctx);
}
